/**
 * Data hooks for admin change-request review (FAV-73 / ENT-44).
 *
 * Endpoints are not yet in the generated OpenAPI surface — calls go through
 * untyped casts, same pattern as users/registrations admin helpers.
 */
import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@favelafood/shared/api/client';
import { ApiError } from '@favelafood/shared/api/errors';

export type ChangeRequestStatus = 'OPEN' | 'APPROVED' | 'REJECTED';
export type ChangeRequestType = 'PERSONAL_DATA' | 'VEHICLE' | 'BANK_ACCOUNT';

export interface ChangeRequestResponse {
  id: string;
  protocol: string;
  type: ChangeRequestType;
  fieldName: string;
  description: string;
  attachmentObjectKey?: string | null;
  status: ChangeRequestStatus;
  adminNotes?: string | null;
  createdAt?: string;
  resolvedAt?: string | null;
  resolvedBy?: string | null;
  userId: string;
  userFullName?: string | null;
  userEmail?: string | null;
}

export interface ChangeRequestPage {
  content: ChangeRequestResponse[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
}

export interface ChangeRequestsListFilters {
  status: ChangeRequestStatus | 'ALL';
  page: number;
  size: number;
}

export interface UpdateChangeRequestStatusVars {
  id: string;
  status: 'APPROVED' | 'REJECTED';
  adminNotes?: string | undefined;
}

export const changeRequestsKeys = {
  all: ['change-requests'] as const,
  list: (filters: ChangeRequestsListFilters) =>
    ['change-requests', 'list', filters] as const,
};

const buildQueryString = (filters: ChangeRequestsListFilters): string => {
  const params = new URLSearchParams();
  if (filters.status !== 'ALL') params.set('status', filters.status);
  params.set('page', String(filters.page));
  params.set('size', String(filters.size));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

const getUntyped = async <T>(path: string): Promise<T> => {
  const result = await api.GET(path as never, {} as never);
  const { data, error } = result as unknown as { data?: T; error?: unknown };
  if (error) {
    if (error instanceof Error) throw error;
    throw new ApiError(500, { message: 'Erro inesperado' }, 'HTTP 500');
  }
  return data as T;
};

const patchUntyped = async <T>(path: string, body: unknown): Promise<T> => {
  const result = await api.PATCH(path as never, { body } as never);
  const { data, error } = result as unknown as { data?: T; error?: unknown };
  if (error) {
    if (error instanceof Error) throw error;
    throw new ApiError(500, { message: 'Erro inesperado' }, 'HTTP 500');
  }
  return data as T;
};

export function useChangeRequestsList(
  filters: ChangeRequestsListFilters,
): UseQueryResult<ChangeRequestPage, Error> {
  return useQuery<ChangeRequestPage, Error>({
    queryKey: changeRequestsKeys.list(filters),
    queryFn: () =>
      getUntyped<ChangeRequestPage>(
        `/api/v1/admin/change-requests${buildQueryString(filters)}`,
      ),
  });
}

export function useUpdateChangeRequestStatus(): UseMutationResult<
  ChangeRequestResponse,
  Error,
  UpdateChangeRequestStatusVars
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, adminNotes }) =>
      patchUntyped<ChangeRequestResponse>(
        `/api/v1/admin/change-requests/${id}/status`,
        { status, adminNotes },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: changeRequestsKeys.all });
    },
  });
}

export async function fetchChangeRequestAttachmentUrl(
  objectKey: string,
): Promise<string> {
  const result = await api.GET('/api/v1/storage/download-url' as never, {
    params: {
      query: {
        bucket: 'change-request-attachments',
        key: objectKey,
      },
    },
  } as never);
  const { data, error } = result as unknown as {
    data?: { url?: string };
    error?: unknown;
  };
  if (error) {
    if (error instanceof Error) throw error;
    throw new ApiError(500, { message: 'Erro inesperado' }, 'HTTP 500');
  }
  if (!data?.url) {
    throw new ApiError(500, { message: 'URL de anexo ausente' }, 'HTTP 500');
  }
  return data.url;
}
